/**
 * Shared login validation helpers used by all portal sign-in flows.
 */

/** Extract the auth payload object from a Frappe/custom login response. */
export function extractAuthPayload(data) {
  if (!data || typeof data !== "object") return null;
  const msg = data.message;
  if (msg && typeof msg === "object" && !Array.isArray(msg)) {
    if (msg.data && typeof msg.data === "object" && !Array.isArray(msg.data)) {
      return { ...msg, ...msg.data };
    }
    return msg;
  }
  return data;
}

/** Pull api_key / api_secret from nested login or OTP payloads. */
export function extractCredentials(source) {
  const roots = [];
  if (source && typeof source === "object") {
    roots.push(source);
    if (source.message && typeof source.message === "object") roots.push(source.message);
    if (source.data && typeof source.data === "object") roots.push(source.data);
    if (source.message?.data && typeof source.message.data === "object") {
      roots.push(source.message.data);
    }
    if (source.message?.auth && typeof source.message.auth === "object") {
      roots.push(source.message.auth);
    }
    if (source.message?.credentials && typeof source.message.credentials === "object") {
      roots.push(source.message.credentials);
    }
  }
  for (const r of roots) {
    const apiKey = r.api_key || r.apiKey;
    const apiSecret = r.api_secret || r.apiSecret;
    if (
      apiKey &&
      apiSecret &&
      !String(apiSecret).toLowerCase().includes("hidden")
    ) {
      return {
        apiKey,
        apiSecret,
        user: r.user || source?.user || source?.message?.user || null,
      };
    }
  }
  return { apiKey: null, apiSecret: null, user: null };
}

/** True when api_key and a usable api_secret are present. */
export function hasUsableApiCredentials(payload) {
  const { apiKey, apiSecret } = extractCredentials(payload);
  return Boolean(apiKey && apiSecret);
}

/**
 * Login can proceed when the backend returned a user plus API credentials
 * (same contract as dynesourcerapi.author.login).
 */
export function hasDirectLoginPayload(data) {
  const payload = extractAuthPayload(data);
  const { apiKey, apiSecret, user } = extractCredentials(data);
  const resolvedUser = user || payload?.user;
  if (!resolvedUser) return false;
  return Boolean(apiKey && apiSecret);
}

export function isInvalidLoginResponse(data) {
  if (!data) return true;
  const messageText =
    typeof data?.message === "string"
      ? data.message
      : typeof data?.message?.message === "string"
        ? data.message.message
        : typeof data?.message?.error === "string"
          ? data.message.error
          : "";
  if (!messageText) return false;
  return (
    messageText === "Invalid login credentials" ||
    messageText.toLowerCase().includes("invalid")
  );
}

/** True when the server is asking for a login OTP (not used for admin). */
export function isTwoFactorChallenge(data) {
  const payload = extractAuthPayload(data);
  return Boolean(payload?.two_factor_required);
}
